import {
  DEFAULT_FORMAT_OPTIONS,
  FormatOptions,
  LocaleCode,
  LocaleCodeMap,
  LocaleDisplayNameMap,
  MAX_NUMBER_DECIMAL_PLACES,
  MIN_NUMBER_DECIMAL_PLACES,
  NumberNegativeStyle,
  formatNumberColumnValue,
} from '@doss/common-all'
import { useMemo } from 'react'
import { InputWrapper } from '~/@/components/inputs/base/input-wrapper'
import { Dropdown } from '~/@/components/inputs/selection/dropdown'
import { Toggle } from '~/@/components/inputs/standalone/toggle'
import { ShortTextInput } from '~/@/components/inputs/text/short-text-input'
import { ToggleGroup } from '~/@/components/ui/toggle-group'
import { Currency, FormatPreset } from '~/common/generated/apollo-operations'
import { TranslationKeys } from '~/common/generated/i18n.generated'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'

const NUMBER_PRESETS: ReadonlyArray<{
  value: FormatPreset
  labelKey: TranslationKeys
}> = [
  {
    value: FormatPreset.NUMBER_PLAIN,
    labelKey: 'table.create_column.format.preset_options.plain',
  },
  {
    value: FormatPreset.NUMBER_INTEGER,
    labelKey: 'table.create_column.format.preset_options.integer',
  },
  {
    value: FormatPreset.NUMBER_DECIMAL,
    labelKey: 'table.create_column.format.preset_options.decimal',
  },
  {
    value: FormatPreset.NUMBER_PERCENT,
    labelKey: 'table.create_column.format.preset_options.percent',
  },
  {
    value: FormatPreset.NUMBER_CURRENCY,
    labelKey: 'table.create_column.format.preset_options.currency',
  },
  {
    value: FormatPreset.NUMBER_UNIT,
    labelKey: 'table.create_column.format.preset_options.unit',
  },
]

const PREVIEW_VALUE = 1234.56

const CURRENCY_OPTIONS = Object.values(Currency).map((currency) => ({
  value: currency,
  label: currency,
}))

const LOCALE_OPTIONS = (
  Object.keys(LocaleDisplayNameMap) as Array<keyof typeof LocaleDisplayNameMap>
).map((locale) => ({
  value: LocaleCodeMap[locale],
  label: LocaleDisplayNameMap[locale],
}))

type NumberOptionField =
  | 'decimalPlaces'
  | 'thousandsSeparator'
  | 'negativeStyle'
  | 'currency'
  | 'locale'
  | 'unit'

const PRESET_FIELDS: Partial<Record<FormatPreset, ReadonlyArray<NumberOptionField>>> = {
  [FormatPreset.NUMBER_PLAIN]: ['decimalPlaces', 'thousandsSeparator', 'negativeStyle'],
  [FormatPreset.NUMBER_INTEGER]: ['thousandsSeparator', 'negativeStyle'],
  [FormatPreset.NUMBER_DECIMAL]: ['decimalPlaces', 'thousandsSeparator', 'negativeStyle'],
  [FormatPreset.NUMBER_PERCENT]: ['decimalPlaces', 'thousandsSeparator', 'negativeStyle'],
  [FormatPreset.NUMBER_CURRENCY]: ['currency', 'locale', 'decimalPlaces', 'negativeStyle'],
  [FormatPreset.NUMBER_UNIT]: ['unit', 'decimalPlaces', 'thousandsSeparator'],
}

const isNumberPreset = (preset: FormatPreset | null): boolean => {
  return (
    preset === FormatPreset.NUMBER_PLAIN ||
    preset === FormatPreset.NUMBER_INTEGER ||
    preset === FormatPreset.NUMBER_DECIMAL ||
    preset === FormatPreset.NUMBER_PERCENT ||
    preset === FormatPreset.NUMBER_CURRENCY ||
    preset === FormatPreset.NUMBER_UNIT
  )
}

type NumberFormatOptions = {
  decimalPlaces?: number
  thousandsSeparator?: boolean
  negativeStyle?: NumberNegativeStyle
  currency?: string
  locale?: string
  unit?: string
}

const getDefaultNumberFormatOptions = (
  preset: FormatPreset,
  orgCurrency: string | undefined,
  orgLocale: string | undefined,
): NumberFormatOptions => {
  const defaults = { ...DEFAULT_FORMAT_OPTIONS[preset] } as NumberFormatOptions

  if (preset === FormatPreset.NUMBER_CURRENCY) {
    return {
      ...defaults,
      currency: orgCurrency ?? defaults.currency,
      locale: orgLocale ?? defaults.locale,
    }
  }

  return defaults
}

const formatPreview = (
  value: number,
  preset: FormatPreset,
  options: NumberFormatOptions,
): string => {
  if (
    options.decimalPlaces !== undefined &&
    (options.decimalPlaces < MIN_NUMBER_DECIMAL_PLACES ||
      options.decimalPlaces > MAX_NUMBER_DECIMAL_PLACES)
  ) {
    return ''
  }

  const previewOptions =
    preset === FormatPreset.NUMBER_UNIT && !options.unit ? { ...options, unit: ' ' } : options

  return formatNumberColumnValue(
    value,
    { formatPreset: preset, formatOptions: previewOptions },
    LocaleCode.EN_US,
  ).trimEnd()
}

export type NumberFormatValue = {
  formatPreset: FormatPreset
  formatOptions: FormatOptions
}

interface NumberFormatFieldsProps {
  /** Current preset (null when the input has no formatting yet) */
  formatPreset: FormatPreset | null
  /** Current options; may be a partial while the user edits */
  formatOptions: FormatOptions | null
  /** Called with the fully-resolved preset + options whenever the user changes anything */
  onChange: (value: NumberFormatValue) => void
  /**
   * When true, the preset dropdown shows a clear button and renders even with no preset selected,
   * letting the host opt out of formatting entirely (used by forms, where formatting is optional).
   */
  allowClear?: boolean
  /** Called when the user clears the preset (only relevant when `allowClear` is set) */
  onClear?: () => void
  /** Organization defaults used to seed the currency preset */
  orgCurrency?: string
  orgLocale?: string
  /** Optional field-level validation errors surfaced by the host form */
  errors?: { unit?: string; decimalPlaces?: string }
  /** Notifies the host form that a field was blurred (for touched-based error display) */
  onBlurField?: (field: 'unit' | 'decimalPlaces') => void
}

/**
 * Headless format configuration for NUMBER/FORMULA values: preset dropdown plus preset-specific
 * options (decimal places, thousands separator, negative style, currency, locale, unit). Fully
 * controlled — it holds no form-library state, so both the Formik (tables) and react-hook-form
 * (forms) hosts can drive it through `formatPreset`/`formatOptions`/`onChange`.
 */
export const NumberFormatFields = ({
  formatPreset,
  formatOptions,
  onChange,
  allowClear,
  onClear,
  orgCurrency,
  orgLocale,
  errors,
  onBlurField,
}: NumberFormatFieldsProps) => {
  const { t } = useTypedTranslation()
  const preset = formatPreset
  const currentOptions = (formatOptions ?? {}) as NumberFormatOptions
  const currencyForLocalePreview = currentOptions.currency ?? orgCurrency ?? Currency.USD

  const presetOptions = useMemo(
    () =>
      NUMBER_PRESETS.map((presetOption) => ({
        value: presetOption.value,
        label: t(presetOption.labelKey),
        endIcon: (
          <code className="text-muted-foreground">
            {formatPreview(
              PREVIEW_VALUE,
              presetOption.value,
              getDefaultNumberFormatOptions(presetOption.value, orgCurrency, orgLocale),
            )}
          </code>
        ),
      })),
    [orgCurrency, orgLocale, t],
  )
  const localeOptions = useMemo(() => {
    const optionsWithPreview = LOCALE_OPTIONS.map((option) => ({
      ...option,
      endIcon: (
        <code className="text-muted-foreground">
          {formatPreview(PREVIEW_VALUE, FormatPreset.NUMBER_CURRENCY, {
            currency: currencyForLocalePreview,
            locale: option.value,
            negativeStyle: NumberNegativeStyle.STANDARD,
          })}
        </code>
      ),
    }))

    if (!orgLocale) {
      return optionsWithPreview
    }

    const organizationOption = optionsWithPreview.find((option) => option.value === orgLocale)
    if (!organizationOption) {
      return optionsWithPreview
    }

    return [
      organizationOption,
      ...optionsWithPreview.filter((option) => option.value !== orgLocale),
    ]
  }, [currencyForLocalePreview, orgLocale])

  const activePreset = isNumberPreset(preset) ? (preset as FormatPreset) : null
  const enabledFields = activePreset ? (PRESET_FIELDS[activePreset] ?? []) : []
  const options = activePreset
    ? enabledFields.reduce<NumberFormatOptions>(
        (normalizedOptions, field) => {
          if (currentOptions[field] === undefined) {
            return normalizedOptions
          }

          return { ...normalizedOptions, [field]: currentOptions[field] }
        },
        getDefaultNumberFormatOptions(activePreset, orgCurrency, orgLocale),
      )
    : {}

  const setOptions = (patch: Partial<NumberFormatOptions>) => {
    if (!activePreset) {
      return
    }
    onChange({
      formatPreset: activePreset,
      formatOptions: { ...options, ...patch } as FormatOptions,
    })
  }

  const onPresetChange = (nextPreset: FormatPreset | undefined) => {
    if (!nextPreset) {
      onClear?.()
      return
    }
    const defaults = getDefaultNumberFormatOptions(nextPreset, orgCurrency, orgLocale)
    if (nextPreset === FormatPreset.NUMBER_UNIT) {
      defaults.unit = ''
    }
    // cmdk's Command moves focus to a newly-mounted sibling Command's input whenever the
    // currently focused element has the `cmdk-input` attribute. Blurring the preset input
    // first prevents the newly mounted currency/locale dropdowns from auto-opening.
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }
    onChange({ formatPreset: nextPreset, formatOptions: defaults as FormatOptions })
  }

  return (
    <div className="flex flex-col gap-3">
      <h4>{t('table.create_column.format.section_title')}</h4>

      <Dropdown<FormatPreset>
        label={t('table.create_column.format.preset')}
        onChange={onPresetChange}
        options={presetOptions}
        showClearButton={allowClear ?? false}
        value={activePreset ?? undefined}
      />

      {activePreset && enabledFields.includes('decimalPlaces') && (
        <ShortTextInput
          errorText={errors?.decimalPlaces}
          label={t('table.create_column.format.decimal_places')}
          max={MAX_NUMBER_DECIMAL_PLACES}
          min={MIN_NUMBER_DECIMAL_PLACES}
          onBlur={() => onBlurField?.('decimalPlaces')}
          onChange={(event) => {
            const raw = event.target.value
            const decimalPlaces =
              raw === ''
                ? MIN_NUMBER_DECIMAL_PLACES
                : Math.max(MIN_NUMBER_DECIMAL_PLACES, parseInt(raw, 10) || 0)
            setOptions({ decimalPlaces })
          }}
          required
          type="number"
          value={String(options.decimalPlaces ?? 2)}
        />
      )}

      {enabledFields.includes('currency') && (
        <div className="flex flex-col gap-3">
          <Dropdown
            label={t('table.create_column.format.currency')}
            onChange={(currency) => {
              if (currency) {
                setOptions({ currency })
              }
            }}
            options={CURRENCY_OPTIONS}
            showClearButton={false}
            value={options.currency ?? orgCurrency}
          />
          {enabledFields.includes('locale') && (
            <Dropdown
              label={t('table.create_column.format.locale')}
              onChange={(locale) => {
                if (locale) {
                  setOptions({ locale })
                }
              }}
              options={localeOptions}
              showClearButton={false}
              value={options.locale ?? orgLocale}
            />
          )}
        </div>
      )}

      {enabledFields.includes('unit') && (
        <ShortTextInput
          errorText={errors?.unit}
          helperText={t('table.create_column.format.unit_helper')}
          label={t('table.create_column.format.unit')}
          onBlur={() => onBlurField?.('unit')}
          onChange={(event) => setOptions({ unit: event.target.value })}
          required
          value={options.unit ?? ''}
        />
      )}

      {enabledFields.includes('thousandsSeparator') && (
        <Toggle
          checked={options.thousandsSeparator ?? false}
          label={t('table.create_column.format.thousands_separator')}
          onCheckedChange={(checked) => setOptions({ thousandsSeparator: Boolean(checked) })}
        />
      )}

      {enabledFields.includes('negativeStyle') && (
        <InputWrapper label={t('table.create_column.format.negative_style')}>
          <ToggleGroup
            className="w-full max-w-sm"
            itemClassName="flex-1"
            items={[
              {
                value: NumberNegativeStyle.STANDARD,
                label: t('table.create_column.format.negative_style_options.standard'),
              },
              {
                value: NumberNegativeStyle.ACCOUNTING,
                label: t('table.create_column.format.negative_style_options.accounting'),
              },
            ]}
            onValueChange={(next) => {
              if (
                next === NumberNegativeStyle.ACCOUNTING ||
                next === NumberNegativeStyle.STANDARD
              ) {
                setOptions({ negativeStyle: next })
              }
            }}
            type="single"
            value={options.negativeStyle ?? NumberNegativeStyle.STANDARD}
          />
        </InputWrapper>
      )}

      {activePreset && (
        <div className="flex items-center justify-between gap-2 rounded border border-dashed border-gray-4 bg-gray-2 px-3 py-2 text-xs">
          <span>{t('table.create_column.format.preview')}</span>
          <code className="min-w-0 truncate text-end text-gray-12">
            {formatPreview(-PREVIEW_VALUE, activePreset, options)}
          </code>
        </div>
      )}
    </div>
  )
}
